import { Controller } from "@hotwired/stimulus"
import { MAX_PAGE_COUNT, TERM_SHEET } from '../../oms/utils/constants'

export default class extends Controller {
  static targets = ["backdrop", "modal", "reportCheckbox", "selectAll", "allReportsCheckbox", "summary", "countBadge", "securityDocument"]
  static values = {
    open: Boolean,
    quoteId: Number,
    updateUrl: String
  }

  connect() {
    // Listen for custom event to open modal
    this.openModalHandler = (event) => {
      this.open(event)
    }
    document.addEventListener('open-report-modal', this.openModalHandler)

    // Initialize local storage for unpersisted quotes
    this.isNewQuote = !this.quoteIdValue || this.quoteIdValue === 0
    this.localSelections = {
      quoteDocuments: {},
      securityDocuments: {}
    }

    // Initialize tracking flags for "All reports" and "Offering Documents" state changes
    this.allReportsWasSelected = false
    this.allReportsStateChanged = false
    this.offeringDocumentsWasSelected = false
    this.offeringDocumentsStateChanged = false
    this.isRestoringState = false

    // Store initial state for cancel functionality
    this.storeInitialState()

    // Only update count on connect for new quotes (where server count isn't reliable)
    // For persisted quotes, trust the server-rendered count until the modal is opened
    if (this.isNewQuote) {
      this.updateCount()
    }
  }

  // Get CSRF token with robust error handling
  // This method handles edge cases like missing meta tags or stale tokens from Turbo cache
  getCSRFToken() {
    const meta = document.querySelector('[name="csrf-token"]')
    const token = meta?.content

    if (!token) {
      console.error('CSRF token meta tag not found or empty. This will likely cause request failures.')
      console.error('Meta tag element:', meta)
      console.error('Page may have been restored from cache with stale session.')
    }

    return token || ''
  }

  // Handle response errors with special handling for CSRF failures
  async handleResponseError(response, context = 'request') {
    console.error(`Response error in ${context}:`, {
      status: response.status,
      statusText: response.statusText,
      headers: Object.fromEntries(response.headers.entries())
    })

    // Check if this is a CSRF token error
    // Rails returns 422 with HTML content when CSRF verification fails
    const contentType = response.headers.get('content-type') || ''
    const isHtmlResponse = contentType.includes('text/html')
    const isCsrfError = (response.status === 422 || response.status === 401) && isHtmlResponse

    if (isCsrfError) {
      console.error('CSRF token verification failed. Session may have expired or page was cached.')

      // Show user-friendly error message
      const shouldReload = confirm(
        'Your session may have expired. Would you like to reload the page?\n\n' +
        'Click OK to reload and try again, or Cancel to stay on this page.'
      )

      if (shouldReload) {
        window.location.reload()
      }

      return { error: 'CSRF verification failed', isCsrfError: true }
    }

    // Try to parse JSON error response
    try {
      const result = await response.json()
      return { error: result.error || 'Request failed', result }
    } catch (e) {
      // If JSON parsing fails, return generic error
      console.error('Failed to parse error response as JSON:', e)
      return { error: `Request failed with status ${response.status}` }
    }
  }

  disconnect() {
    // Clean up event listener
    if (this.openModalHandler) {
      document.removeEventListener('open-report-modal', this.openModalHandler)
    }
  }

  async open(event) {
    if (event && event.preventDefault) {
      event.preventDefault()
    }
    this.openValue = true

    // Reset tracking flags for All reports and Offering Documents state
    this.allReportsWasSelected = false
    this.allReportsStateChanged = false
    this.offeringDocumentsWasSelected = false
    this.offeringDocumentsStateChanged = false
    this.isRestoringState = false

    // Show the modal by removing hidden class from the controller element
    this.element.classList.remove('hidden')
    document.body.classList.add('overflow-hidden')

    // Set restoration flag for the entire restoration process
    this.isRestoringState = true

    // Rebuild security documents section for dynamic line items (async)
    await this.rebuildSecurityDocumentsSection()

    // Restore selections from hidden fields or local storage
    this.restoreSelections()

    // Apply overrides after security documents are built and selections are restored
    this.applyRestorationOverrides()

    // Clear restoration flag after all restoration is complete
    this.isRestoringState = false

    // Store state when opening
    this.storeInitialState()
  }

  restoreSelections() {
    console.log('Restoring selections...')
    console.log('Quote ID:', this.quoteIdValue)
    console.log('Is new quote:', this.isNewQuote)

    // For persisted quotes, restore from the data attribute on the modal element
    if (this.quoteIdValue && this.quoteIdValue > 0) {
      // Get the quote document data from the modal's data attribute
      const quoteDocData = JSON.parse(this.element.dataset.quoteDocuments || '{}')
      console.log('Quote document data from modal:', quoteDocData)

      if (Object.keys(quoteDocData).length > 0) {
        Object.keys(quoteDocData).forEach(field => {
          const checkbox = this.element.querySelector(`[data-field="${field}"]`)
          if (checkbox) {
            checkbox.checked = quoteDocData[field] === true
            console.log(`Restored ${field} to ${checkbox.checked}`)

            // If this is the All Reports checkbox and it's checked, store that fact
            if (field === 'include_all_reports' && checkbox.checked) {
              console.log('All Reports was previously selected - will apply override after restoration')
              this.allReportsWasSelected = true
            }

            // If this is the Offering Documents checkbox and it's checked, store that fact
            if (field === 'include_offering_documents' && checkbox.checked) {
              console.log('Offering Documents was previously selected - will apply override after restoration')
              this.offeringDocumentsWasSelected = true
            }
          }
        })
      }
    }

    // Also check for hidden fields in the form (for values changed but not saved)
    const form = document.querySelector('form[id*="quote"]')
    if (form) {
      const fields = ['include_indicative_quote', 'include_notes', 'include_issuer_reports', 'include_debt_securities_snapshot', 'include_offering_documents', 'include_all_reports']

      fields.forEach(field => {
        const hiddenField = form.querySelector(`input[name="oms_quote[${field}]"]`)
        const checkbox = this.element.querySelector(`[data-field="${field}"]`)

        if (checkbox) {
          if (hiddenField && hiddenField.value) {
            // Restore from hidden field (overrides database value if present)
            const isChecked = hiddenField.value === '1'
            checkbox.checked = isChecked
            console.log(`Restored ${field} from hidden field: ${isChecked}`)

            // Track if All Reports was selected via hidden field
            if (field === 'include_all_reports' && isChecked) {
              console.log('All Reports was selected via hidden field - will apply override')
              this.allReportsWasSelected = true
            }

            // Track if Offering Documents was selected via hidden field
            if (field === 'include_offering_documents' && isChecked) {
              console.log('Offering Documents was selected via hidden field - will apply override')
              this.offeringDocumentsWasSelected = true
            }
          } else if (this.isNewQuote && this.localSelections.quoteDocuments[field] !== undefined) {
            // Restore from local storage for new quotes
            const isChecked = this.localSelections.quoteDocuments[field]
            checkbox.checked = isChecked
            console.log(`Restored ${field} from local storage: ${isChecked}`)

            // Track if All Reports was selected via local storage
            if (field === 'include_all_reports' && isChecked) {
              console.log('All Reports was selected via local storage - will apply override')
              this.allReportsWasSelected = true
            }

            // Track if Offering Documents was selected via local storage
            if (field === 'include_offering_documents' && isChecked) {
              console.log('Offering Documents was selected via local storage - will apply override')
              this.offeringDocumentsWasSelected = true
            }
          }
        }
      })
    }

    // Note: Security document selections are restored in rebuildSecurityDocumentsSection
    // when the checkboxes are created, using this.persistedSelections

    this.updateCount()

  }

  applyRestorationOverrides() {
    console.log('Applying restoration overrides...')

    // IMPORTANT: Apply All Reports override AFTER everything else is restored and counted
    // This ensures that the override doesn't interfere with the restoration process
    const allReportsCheckbox = this.hasAllReportsCheckboxTarget ? this.allReportsCheckboxTarget : null
    if (allReportsCheckbox && allReportsCheckbox.checked) {
      console.log('All reports is checked after restoration, applying override')
      // Apply the override behavior without triggering the change event
      this.applyAllReportsOverride(true)
    }

    // Check if Offering Documents was selected and apply override
    const offeringDocsCheckbox = this.reportCheckboxTargets.find(cb => cb.dataset.field === 'include_offering_documents')
    if (offeringDocsCheckbox && offeringDocsCheckbox.checked) {
      console.log('Offering Documents is checked after restoration, applying override')
      // Apply the override behavior without triggering the change event
      this.handleOfferingDocumentsChange(true)
    }
  }

  async rebuildSecurityDocumentsSection() {
    const securityDocsContainer = this.element.querySelector('.security-documents-container')

    if (!securityDocsContainer) return

    // Clear existing content and show loading state
    securityDocsContainer.innerHTML = '<p class="text-sm text-neutral-500 italic">Loading security documents...</p>'

    // Collect all security IDs (both persisted and unpersisted)
    const securityIds = []
    const securityInfo = new Map()

    // Check if we have pre-loaded line items data (for show page)
    const preloadedLineItems = this.element.dataset.quoteLineItems
    if (preloadedLineItems && preloadedLineItems !== '[]') {
      // Use pre-loaded data from the server (show page context)
      const lineItemsData = JSON.parse(preloadedLineItems)
      console.log('Using preloaded line items:', lineItemsData)

      lineItemsData.forEach(item => {
        securityIds.push(item.security_id.toString())
        securityInfo.set(item.security_id.toString(), {
          lineItemId: item.id.toString(),
          securityName: item.security_name,
          isin: item.security_isin,
          securityId: item.security_id.toString(),
          isPersisted: true
        })
      })
    } else {
      // Find line items in the form (edit/new page context)
      const lineItems = document.querySelectorAll('.quote-line-item:not([style*="display: none"])')

      if (lineItems.length === 0) {
        securityDocsContainer.innerHTML = '<p class="text-sm text-neutral-500 italic">No securities added yet</p>'
        return
      }

      lineItems.forEach((lineItem, index) => {
        const securityIdField = lineItem.querySelector('[name*="[security_id]"]')
        const lineItemIdField = lineItem.querySelector('[name*="[id]"][name*="line_items"]')
        const securityDisplay = lineItem.querySelector('.security-display')

        if (securityIdField && securityIdField.value) {
          const securityId = securityIdField.value
          securityIds.push(securityId)

          // Store info about each line item
          // For unpersisted items, use security ID as the identifier since that's what we'll map on the backend
          const lineItemId = lineItemIdField && lineItemIdField.value ? lineItemIdField.value : `new_${securityId}`
          const securityName = securityDisplay?.querySelector('.font-medium')?.textContent || 'Unknown Security'
          const isin = securityDisplay?.querySelector('.text-neutral-500')?.textContent?.replace('ISIN: ', '') || ''

          securityInfo.set(securityId, {
            lineItemId: lineItemId,
            securityName: securityName,
            isin: isin,
            securityId: securityId,
            isPersisted: !!lineItemIdField?.value
          })
        }
      })
    }

    if (securityIds.length === 0) {
      securityDocsContainer.innerHTML = '<p class="text-sm text-neutral-500 italic">No securities added yet</p>'
      return
    }

    try {
      // Fetch documents for all securities, including quote ID to get selected documents
      const requestData = {
        security_ids: securityIds,
        quote_id: this.quoteIdValue > 0 ? this.quoteIdValue : null
      }

      const response = await fetch('/oms/security_documents', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': this.getCSRFToken()
        },
        body: JSON.stringify(requestData)
      })

      if (!response.ok) {
        const errorInfo = await this.handleResponseError(response, 'fetch security documents')
        if (errorInfo.isCsrfError) {
          // CSRF error already handled, just return
          return
        }
        throw new Error(errorInfo.error || 'Failed to fetch security documents')
      }

      const data = await response.json()

      // Store the selected documents information
      if (data.selected_documents) {
        this.persistedSelections = data.selected_documents
        console.log('Loaded persisted selections:', this.persistedSelections)
      }

      // Clear container and build UI
      securityDocsContainer.innerHTML = ''

      // Build document sections for each security
      data.securities.forEach(securityData => {
        const info = securityInfo.get(securityData.security_id.toString())
        if (!info) return

        const securitySection = document.createElement('div')
        securitySection.className = 'border border-neutral-200 rounded-md p-3 mb-3'

        let html = `
          <div class="text-sm font-medium text-neutral-900 mb-2">
            ${securityData.name || info.securityName}
            <span class="text-xs text-neutral-500 ml-2">(${securityData.isin || info.isin})</span>
          </div>
        `

        if (securityData.documents && securityData.documents.length > 0) {
          html += '<div class="space-y-2 ml-4">'

          securityData.documents.forEach(doc => {
            // Check if this document is already selected
            let isSelected = false

            if (info.isPersisted && this.persistedSelections) {
              // For persisted line items, check the server data
              const key = `${info.lineItemId}_${doc.id}`
              const secKey = `${securityData.security_id}_${doc.id}`

              // Debug logging
              console.log(`Checking selection for doc ${doc.id} on line item ${info.lineItemId}:`)
              console.log(`  Key: ${key}, Value: ${this.persistedSelections[key]}`)
              console.log(`  SecKey: ${secKey}, Value:`, this.persistedSelections[secKey])

              isSelected = this.persistedSelections[key] === true ||
                          (this.persistedSelections[secKey] && this.persistedSelections[secKey].selected)
            } else {
              // For unpersisted items, check local selections
              isSelected = this.isDocumentSelected(info.lineItemId, doc.id)
            }

            console.log(`Document ${doc.id} for line ${info.lineItemId}: isSelected = ${isSelected}`)

            // Check if document is too large (>10 pages) - TERM_SHEET is always allowed
            const isTooLarge = doc.document_type !== TERM_SHEET && doc.page_count && doc.page_count > MAX_PAGE_COUNT
            const hasNoPageCount = doc.page_count === null
            const isDisabled = isTooLarge || hasNoPageCount
            const warningText = isTooLarge
              ? `Document is ${doc.page_count} pages. Large documents may cause PDF generation to fail. Please download the document from the security page, if required.`
              : hasNoPageCount
              ? `This document has a problem and cannot be included in the quote.`
              : ''

            html += `
              <label class="flex items-start ${isTooLarge ? 'opacity-60 bg-neutral-50' : ''}">
                <input type="checkbox"
                       name="security_documents[${info.lineItemId}][]"
                       value="${doc.id}"
                       ${isSelected && !isDisabled ? 'checked' : ''}
                       ${isDisabled ? 'disabled' : ''}
                       class="h-4 w-4 mt-0.5 ${isDisabled ? 'text-neutral-400' : 'text-info-600 focus:ring-info-500'} border-neutral-300 rounded"
                       data-action="change->oms--oms--report-selection-modal#updateCount"
                       data-oms--oms--report-selection-modal-target="securityDocument"
                       data-line-item-id="${info.lineItemId}"
                       data-security-id="${securityData.security_id}"
                       data-is-persisted="${info.isPersisted}">
                <div class="ml-3 flex-1">
                  <span class="text-sm ${isDisabled ? 'text-neutral-500' : 'text-neutral-900'}">
                    ${doc.name}
                  </span>
                  <div class="text-xs text-neutral-500">
                    ${doc.document_type ? doc.document_type.replace(/_/g, ' ') : 'Document'} • ${doc.page_count || 0} pages
                  </div>
                  ${warningText ? `<div class="text-xs text-warning-600 mt-1">${warningText}</div>` : ''}
                </div>
              </label>
            `
          })

          html += '</div>'
        } else {
          html += '<div class="text-xs text-neutral-500 italic ml-4">No documents available for this security</div>'
        }

        securitySection.innerHTML = html
        securityDocsContainer.appendChild(securitySection)
      })

      // Store the fetched security data for later use
      this.securityDocumentData = data.securities
      // Also store the collected security info for use in updateDocumentSelectionDisplay
      this.collectedSecurityInfo = securityInfo
      console.log('Stored security document data:', this.securityDocumentData)
      console.log('Stored collected security info:', this.collectedSecurityInfo)

    } catch (error) {
      console.error('Error fetching security documents:', error)
      securityDocsContainer.innerHTML = '<p class="text-sm text-error-500">Failed to load security documents. Please try again.</p>'
    }
  }

  isDocumentSelected(lineItemId, documentId) {
    // For persisted quotes on edit page, we need to check the actual database state
    // We'll fetch this information from the page or make an API call

    // First check local selections (for changes made in this session)
    if (this.localSelections.securityDocuments[lineItemId]) {
      return this.localSelections.securityDocuments[lineItemId].includes(documentId.toString())
    }

    // For persisted line items, check if the document is already selected in the database
    // We need to get this info from the server or from the page
    if (this.quoteIdValue && this.quoteIdValue > 0) {
      // Check if we have stored selection data
      return this.checkPersistedSelection(lineItemId, documentId)
    }

    return false
  }

  checkPersistedSelection(lineItemId, documentId) {
    // We'll store the persisted selections when we fetch the documents
    if (!this.persistedSelections) {
      this.persistedSelections = {}
    }

    const key = `${lineItemId}_${documentId}`
    return this.persistedSelections[key] === true
  }

  close(event) {
    if (event) event.preventDefault()
    this.openValue = false
    this.element.classList.add('hidden')
    document.body.classList.remove('overflow-hidden')
  }

  async save(event) {
    event.preventDefault()

    // Collect selected documents
    const documentData = {}

    // Collect quote-level documents - ensure all fields are sent
    const quoteFields = ['include_indicative_quote', 'include_notes', 'include_issuer_reports', 'include_debt_securities_snapshot', 'include_offering_documents', 'include_all_reports']

    // Initialize all fields to false
    quoteFields.forEach(field => {
      documentData[field] = 'false'
    })

    console.log('Report checkbox targets:', this.reportCheckboxTargets.length)

    // FIRST: Handle the "All Reports" checkbox separately (it has its own target)
    if (this.hasAllReportsCheckboxTarget) {
      const allReportsCheckbox = this.allReportsCheckboxTarget
      console.log('All Reports checkbox:', {
        field: allReportsCheckbox.dataset.field,
        checked: allReportsCheckbox.checked,
        disabled: allReportsCheckbox.disabled
      })
      documentData.include_all_reports = allReportsCheckbox.checked ? 'true' : 'false'
      console.log(`Setting include_all_reports to ${allReportsCheckbox.checked ? 'true' : 'false'}`)
    }

    // Then update based on actual checkbox states for other report checkboxes
    this.reportCheckboxTargets.forEach(checkbox => {
      console.log('Checkbox:', {
        field: checkbox.dataset.field,
        checked: checkbox.checked,
        disabled: checkbox.disabled,
        id: checkbox.id,
        name: checkbox.name,
        allReportsOverridden: checkbox.dataset.allReportsOverridden
      })

      // Handle all other report checkboxes (exclude All Reports as it's handled separately)
      if (checkbox.dataset.field && checkbox.dataset.field !== 'include_all_reports') {
        const fieldName = checkbox.dataset.field
        const cleanFieldName = fieldName.replace(/^:/, '')

        // When "All Reports" is selected, these individual fields should be true
        // (because "All Reports" means all documents are included)
        if (documentData.include_all_reports === 'true') {
          documentData[cleanFieldName] = 'true'
          console.log(`Setting ${cleanFieldName} to true (All Reports selected)`)
        } else {
          // If overridden by All Reports, use the stored actual state
          let actualChecked = checkbox.checked
          if (checkbox.dataset.allReportsOverridden === 'true') {
            actualChecked = checkbox.dataset.actualCheckedByAllReports === 'true'
            console.log(`Using stored actual state for ${cleanFieldName}: ${actualChecked}`)
          }

          documentData[cleanFieldName] = actualChecked ? 'true' : 'false'
          console.log(`Setting ${cleanFieldName} to ${actualChecked ? 'true' : 'false'}`)
        }

        // Store locally for new quotes
        if (this.isNewQuote) {
          this.localSelections.quoteDocuments[cleanFieldName] = documentData[cleanFieldName] === 'true'
        }
      }
    })

    console.log('Final document data being sent:', JSON.stringify(documentData, null, 2))

    // Collect security-level documents
    // Handle both persisted and unpersisted line items
    const securityDocs = {}
    const unpersistedSecurityDocs = {}

    // Only collect individual security documents if neither "All reports" nor "Offering Documents" is selected
    // When include_all_reports or include_offering_documents is true, the server will automatically include all security documents
    const allReportsSelected = documentData.include_all_reports === 'true'
    const offeringDocumentsSelected = documentData.include_offering_documents === 'true'

    if (allReportsSelected) {
      console.log('All reports selected - skipping individual security document collection')
    } else if (offeringDocumentsSelected) {
      console.log('Offering Documents selected - skipping individual security document collection')
    }

    if (this.hasSecurityDocumentTarget && !allReportsSelected && !offeringDocumentsSelected) {
      console.log('Collecting security documents (neither All reports nor Offering Documents selected)...')

      this.securityDocumentTargets.forEach(checkbox => {
        const lineItemId = checkbox.dataset.lineItemId
        const isPersisted = checkbox.dataset.isPersisted === 'true'
        const securityId = checkbox.dataset.securityId

        // Check if this checkbox is visually overridden by either Offering Documents or All Reports
        let isActuallyChecked = false
        if (checkbox.dataset.visuallyOverridden === 'true') {
          // Overridden by Offering Documents - use the stored actual state
          isActuallyChecked = checkbox.dataset.actualChecked === 'true'
          console.log(`Security doc ${checkbox.value} is visually overridden by Offering Documents. Actual state: ${isActuallyChecked}`)
        } else if (checkbox.dataset.allReportsOverridden === 'true') {
          // Overridden by All Reports - use the stored actual state
          isActuallyChecked = checkbox.dataset.actualCheckedByAllReports === 'true'
          console.log(`Security doc ${checkbox.value} is overridden by All Reports. Actual state: ${isActuallyChecked}`)
        } else {
          // Use the current checked state
          isActuallyChecked = checkbox.checked
        }

        if (isActuallyChecked) {
          if (isPersisted) {
            // For persisted line items, use the actual line item ID
            if (!securityDocs[lineItemId]) {
              securityDocs[lineItemId] = []
            }
            securityDocs[lineItemId].push(checkbox.value)
            console.log(`Including persisted security doc ${checkbox.value} for line item ${lineItemId}`)
          } else {
            // For unpersisted line items, track by security ID
            // Extract security ID from the lineItemId (format: "new_<securityId>")
            const actualSecurityId = securityId || lineItemId.replace('new_', '')
            if (!unpersistedSecurityDocs[actualSecurityId]) {
              unpersistedSecurityDocs[actualSecurityId] = []
            }
            unpersistedSecurityDocs[actualSecurityId].push(checkbox.value)
            console.log(`Including unpersisted security doc ${checkbox.value} for security ${actualSecurityId}`)
          }
        }

        // Store in local selections for future reference
        if (!this.localSelections.securityDocuments[lineItemId]) {
          this.localSelections.securityDocuments[lineItemId] = []
        }
        if (isActuallyChecked && !this.localSelections.securityDocuments[lineItemId].includes(checkbox.value)) {
          this.localSelections.securityDocuments[lineItemId].push(checkbox.value)
        }
      })
    }

    if (Object.keys(securityDocs).length > 0) {
      documentData.security_documents = securityDocs
    }

    if (Object.keys(unpersistedSecurityDocs).length > 0) {
      documentData.unpersisted_security_documents = unpersistedSecurityDocs
      console.log('Unpersisted security documents:', unpersistedSecurityDocs)
    }

    console.log('Final document data:', documentData)
    console.log('About to check if new quote...')

    try {
      console.log('Entering try block...')
      console.log('Is new quote?', this.isNewQuote)
      console.log('Quote ID value:', this.quoteIdValue)

      // For new quotes, store locally and update hidden fields
      if (this.isNewQuote) {
        console.log('Taking new quote path...')
        this.updateHiddenFields(documentData)

        // Update the document count on the page
        const documentCount = document.getElementById('document-count')
        if (documentCount) {
          const count = this.calculateDocumentCount(documentData)
          documentCount.textContent = count
        }

        // Update the document selection widget display
        this.updateDocumentSelectionDisplay(documentData)

        // Close modal without reloading
        this.updateCount()
        this.close()
        return
      }

      console.log('Taking persisted quote AJAX path...')
    } catch (error) {
      console.error('Error in done() method:', error)
      console.error('Error stack:', error.stack)
      return
    }

    // For persisted quotes, make AJAX request to update documents
    try {
      console.log('Making AJAX request to update documents...')
      console.log('URL:', this.updateUrlValue)
      console.log('Document data being sent:', documentData)
      console.log('CSRF token present:', !!this.getCSRFToken())

      const response = await fetch(this.updateUrlValue, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': this.getCSRFToken()
        },
        body: JSON.stringify(documentData)
      })

      console.log('Response status:', response.status)
      console.log('Response headers:', Object.fromEntries(response.headers.entries()))

      // Check for errors before trying to parse JSON
      if (!response.ok) {
        const errorInfo = await this.handleResponseError(response, 'update documents')
        if (errorInfo.isCsrfError) {
          // CSRF error already handled (user will reload or stay on page)
          return
        }
        // Show the error message from the server
        alert(errorInfo.error || 'Failed to update document selection')
        return
      }

      // Only parse JSON if response is ok
      const result = await response.json()
      console.log('Response data:', result)

      if (result.success) {
        console.log('AJAX request successful, updating display...')
        // States are stored in data attributes now, no need to clear

        // Update the data-quote-documents attribute with the new values
        // This ensures that if the modal is reopened, it has the correct data
        const currentData = JSON.parse(this.element.dataset.quoteDocuments || '{}')
        const updatedData = {
          ...currentData,
          include_indicative_quote: documentData.include_indicative_quote === 'true',
          include_notes: documentData.include_notes === 'true',
          include_issuer_reports: documentData.include_issuer_reports === 'true',
          include_debt_securities_snapshot: documentData.include_debt_securities_snapshot === 'true',
          include_offering_documents: documentData.include_offering_documents === 'true',
          include_all_reports: documentData.include_all_reports === 'true'
        }
        this.element.dataset.quoteDocuments = JSON.stringify(updatedData)
        console.log('Updated data-quote-documents:', updatedData)

        // Also update the widget's data attribute for document display
        const documentWidget = document.querySelector('[data-controller*="document-selection-widget"]')
        if (documentWidget) {
          documentWidget.dataset.quoteDocuments = JSON.stringify(updatedData)
          console.log('Updated widget data-quote-documents:', updatedData)
        }

        // Update the document count on the page
        const documentCount = document.getElementById('document-count')
        if (documentCount) {
          documentCount.textContent = result.selected_count || 0
        }

        // Update hidden fields for form submission
        this.updateHiddenFields(documentData)

        // Update the document selection widget display
        this.updateDocumentSelectionDisplay(documentData)

        // Update the count and close modal
        this.updateCount()
        this.close()

        // Don't reload the page for edit mode - let the user save manually
        if (!window.location.pathname.includes('/edit')) {
          window.location.reload()
        }
      } else {
        alert(result.error || 'Failed to update document selection')
      }
    } catch (error) {
      console.error('Error updating documents:', error)
      console.error('Error stack:', error.stack)
      alert('An error occurred while updating document selection. Please try refreshing the page.')
    }
  }

  updateHiddenFields(documentData) {
    // Find or create hidden fields for document selections
    const form = document.querySelector('form[id*="quote"]')
    if (!form) return

    // First, remove any existing document-related hidden fields to avoid duplicates
    form.querySelectorAll('input[type="hidden"][name*="include_indicative_quote"]').forEach(field => field.remove())
    form.querySelectorAll('input[type="hidden"][name*="include_notes"]').forEach(field => field.remove())
    form.querySelectorAll('input[type="hidden"][name*="include_issuer_reports"]').forEach(field => field.remove())
    form.querySelectorAll('input[type="hidden"][name*="include_debt_securities_snapshot"]').forEach(field => field.remove())
    form.querySelectorAll('input[type="hidden"][name*="include_offering_documents"]').forEach(field => field.remove())
    form.querySelectorAll('input[type="hidden"][name*="include_all_reports"]').forEach(field => field.remove())
    form.querySelectorAll('input[name*="[line_item_document_ids]"]').forEach(field => field.remove())
    form.querySelectorAll('input[name*="[unpersisted_security_documents]"]').forEach(field => field.remove())
    form.querySelectorAll('input[name="document_selection_mode"]').forEach(field => field.remove())

    // Add document_selection_mode flag to indicate user made explicit selections
    // This tells the controller to use manual selections instead of auto-selection
    const selectionModeField = document.createElement('input')
    selectionModeField.type = 'hidden'
    selectionModeField.name = 'document_selection_mode'
    selectionModeField.value = 'manual'
    form.appendChild(selectionModeField)

    // Update quote-level document fields
    Object.keys(documentData).forEach(field => {
      if (field === 'security_documents' || field === 'unpersisted_security_documents') return // Handle separately

      const hiddenField = document.createElement('input')
      hiddenField.type = 'hidden'
      hiddenField.name = `oms_quote[${field}]`
      hiddenField.value = documentData[field] === 'true' ? '1' : '0'
      form.appendChild(hiddenField)
    })

    // Handle persisted security documents
    if (documentData.security_documents) {
      Object.keys(documentData.security_documents).forEach(lineItemId => {
        documentData.security_documents[lineItemId].forEach(docId => {
          const hiddenField = document.createElement('input')
          hiddenField.type = 'hidden'
          hiddenField.name = `oms_quote[line_item_document_ids][${lineItemId}][]`
          hiddenField.value = docId
          form.appendChild(hiddenField)
        })
      })
    }

    // Handle unpersisted security documents (documents for securities not yet saved to DB)
    if (documentData.unpersisted_security_documents) {
      Object.keys(documentData.unpersisted_security_documents).forEach(securityId => {
        documentData.unpersisted_security_documents[securityId].forEach(docId => {
          const hiddenField = document.createElement('input')
          hiddenField.type = 'hidden'
          hiddenField.name = `oms_quote[unpersisted_security_documents][${securityId}][]`
          hiddenField.value = docId
          form.appendChild(hiddenField)
        })
      })
    }
  }

  // Document count calculation for form submission
  // This function calculates the document count based on the documentData object that will be
  // submitted to the server. It's used after the user clicks "Done" to update the count displayed
  // on the main form page. Unlike updateCount() which works with DOM elements, this works with
  // the structured data object. It needs to handle the same special cases (All reports includes
  // everything, Offering Documents includes all security docs) but uses the data structure rather
  // than DOM state. Similar logic exists in updateCount() for real-time UI updates and in Ruby's
  // selected_report_count for server-side display.
  calculateDocumentCount(documentData) {
    let count = 0

    // When "All reports" is selected, count ALL available documents
    if (documentData.include_all_reports === 'true') {
      // Count all quote-level documents (real documents)
      count += 3 // Indicative Quote Summary, Client Notes, Debt Securities Snapshot
      count += 1 // Issuer Reports (always included with All Reports)

      // Count ALL security documents
      if (this.securityDocumentData && Array.isArray(this.securityDocumentData)) {
        this.securityDocumentData.forEach(securityData => {
          if (securityData.documents && Array.isArray(securityData.documents)) {
            count += securityData.documents.length
          }
        })
      }

      console.log(`calculateDocumentCount: All reports selected, total count: ${count}`)
      return count
    }

    // Normal counting when "All reports" is not selected
    // Count quote-level documents (excluding Offering Documents which is not a real document)
    if (documentData.include_indicative_quote === 'true') count++
    if (documentData.include_notes === 'true') count++
    if (documentData.include_issuer_reports === 'true') count++ // Issuer reports
    if (documentData.include_debt_securities_snapshot === 'true') count++

    // Count security documents
    if (documentData.include_offering_documents === 'true') {
      // When Offering Documents is selected, count ALL available security documents
      // Count from the fetched security document data instead of DOM checkboxes
      if (this.securityDocumentData && Array.isArray(this.securityDocumentData)) {
        this.securityDocumentData.forEach(securityData => {
          if (securityData.documents && Array.isArray(securityData.documents)) {
            count += securityData.documents.length
          }
        })
      }
    } else {
      // Count only the selected security documents when Offering Documents is NOT selected
      if (documentData.security_documents) {
        Object.values(documentData.security_documents).forEach(docs => {
          count += docs.length
        })
      }

      // Also count unpersisted security documents if present (only when not using Offering Documents)
      if (documentData.unpersisted_security_documents) {
        Object.values(documentData.unpersisted_security_documents).forEach(docs => {
          count += docs.length
        })
      }
    }

    return count
  }

  cancel(event) {
    event.preventDefault()
    // Restore initial state
    this.restoreInitialState()
    this.close()
  }

  toggleAll(event) {
    const isChecked = event.target.checked

    // Toggle all quote-level checkboxes (except the All Reports checkbox itself)
    this.reportCheckboxTargets.forEach(checkbox => {
      if (!checkbox.disabled && checkbox !== event.target) {
        checkbox.checked = isChecked
      }
    })

    // Also toggle all security-level checkboxes when All Reports is checked
    if (this.hasSecurityDocumentTarget) {
      this.securityDocumentTargets.forEach(checkbox => {
        if (!checkbox.disabled) {
          // Store actual state for restoration
          if (isChecked && !checkbox.dataset.visuallyOverridden) {
            checkbox.dataset.actualChecked = checkbox.checked ? 'true' : 'false'
          }
          checkbox.checked = isChecked
        }
      })
    }

    this.updateCount()
  }

  // Real-time document count for UI display in the modal
  // This function calculates the document count based on the current state of checkboxes in the DOM.
  // It handles special cases like "Offering Documents" (which includes all security docs) and
  // "All reports" (which includes all available docs). This runs on every checkbox change to provide
  // instant feedback without network calls.
  // Similar logic exists in calculateDocumentCount() for form submission and in Ruby's
  // selected_report_count for server-side display.
  updateCount() {
    // Check if "All reports" is selected
    const allReportsCheckbox = this.hasAllReportsCheckboxTarget ? this.allReportsCheckboxTarget : null
    const isAllReportsChecked = allReportsCheckbox && allReportsCheckbox.checked

    // Check if "Offering Documents" is selected
    const offeringDocsCheckbox = this.reportCheckboxTargets.find(cb => cb.dataset.field === 'include_offering_documents')

    let totalChecked = 0

    if (isAllReportsChecked) {
      // When "All reports" is checked, count ALL real documents
      // Count all quote-level documents (real documents, not the All Reports flag itself)
      totalChecked += 3 // Indicative Quote Summary, Client Notes, Debt Securities Snapshot
      totalChecked += 1 // Issuer Reports (always included with All Reports)

      // Count ALL security documents since All Reports includes them all
      if (this.securityDocumentData && Array.isArray(this.securityDocumentData)) {
        this.securityDocumentData.forEach(securityData => {
          if (securityData.documents && Array.isArray(securityData.documents)) {
            totalChecked += securityData.documents.length
          }
        })
      } else if (this.hasSecurityDocumentTarget) {
        // Fallback to DOM checkboxes if data not available
        totalChecked += this.securityDocumentTargets.filter(cb => !cb.disabled).length
      }

      console.log(`updateCount: All reports selected, total: ${totalChecked}`)
    } else {
      // Normal counting logic
      // Count quote-level documents (excluding All Reports and Offering Documents since they're not real documents)
      const checkedQuoteDocs = this.reportCheckboxTargets.filter(cb => {
        return cb.checked && !cb.disabled &&
               cb.dataset.field !== 'include_all_reports' &&
               cb.dataset.field !== 'include_offering_documents'
      }).length

      // Count security-level documents
      let checkedSecurityDocs = 0
      if (this.hasSecurityDocumentTarget) {
        if (offeringDocsCheckbox && offeringDocsCheckbox.checked) {
          // When Offering Documents is checked, count ALL available security documents
          // Count from the fetched security document data instead of DOM checkboxes
          if (this.securityDocumentData && Array.isArray(this.securityDocumentData)) {
            this.securityDocumentData.forEach(securityData => {
              if (securityData.documents && Array.isArray(securityData.documents)) {
                checkedSecurityDocs += securityData.documents.length
              }
            })
          } else {
            // Fallback to DOM checkboxes if data not available (e.g., modal hasn't been opened yet)
            checkedSecurityDocs = this.securityDocumentTargets.filter(cb => !cb.disabled).length
          }
        } else {
          // Count only the actually checked security documents
          checkedSecurityDocs = this.securityDocumentTargets.filter(cb => cb.checked && !cb.disabled).length
        }
      }

      totalChecked = checkedQuoteDocs + checkedSecurityDocs
    }

    // Update summary text in the form
    const summaryText = document.getElementById('report-summary-text')
    const infoBadge = document.getElementById('report-info-badge')

    if (summaryText) {
      if (totalChecked > 0) {
        summaryText.textContent = `${totalChecked} Document${totalChecked !== 1 ? 's' : ''}`
        summaryText.classList.remove('text-neutral-500')
        summaryText.classList.add('text-neutral-700')

        // Show or create info badge
        if (!infoBadge) {
          const badgeHtml = `<span class="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-neutral-100 text-neutral-800" id="report-info-badge">
            <svg class="mr-1 h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
            </svg>
          </span>`
          summaryText.insertAdjacentHTML('afterend', badgeHtml)
        }
      } else {
        summaryText.textContent = 'No documents selected'
        summaryText.classList.remove('text-neutral-700')
        summaryText.classList.add('text-neutral-500')

        // Hide info badge
        if (infoBadge) {
          infoBadge.remove()
        }
      }
    }

    // Update count badge on the page
    const documentCount = document.getElementById('document-count')
    if (documentCount) {
      documentCount.textContent = totalChecked
    }

    // Update select all checkbox (only for quote-level docs)
    if (this.hasSelectAllTarget) {
      const enabledCheckboxes = this.reportCheckboxTargets.filter(cb => !cb.disabled)
      const checkedEnabledCount = enabledCheckboxes.filter(cb => cb.checked).length

      this.selectAllTarget.checked = enabledCheckboxes.length > 0 && checkedEnabledCount === enabledCheckboxes.length
      this.selectAllTarget.indeterminate = checkedEnabledCount > 0 && checkedEnabledCount < enabledCheckboxes.length
    }
  }

  storeInitialState() {
    this.initialState = {
      reports: this.reportCheckboxTargets.map(checkbox => ({
        element: checkbox,
        checked: checkbox.checked
      })),
      securityDocs: this.hasSecurityDocumentTarget ? this.securityDocumentTargets.map(checkbox => ({
        element: checkbox,
        checked: checkbox.checked,
        disabled: checkbox.disabled
      })) : []
    }
  }

  restoreInitialState() {
    if (this.initialState) {
      // First, remove any visual overrides (both Offering Documents and All Reports)
      this.reportCheckboxTargets.forEach(checkbox => {
        if (checkbox.dataset.allReportsOverridden === 'true') {
          // Remove All Reports override
          delete checkbox.dataset.allReportsOverridden
          delete checkbox.dataset.actualCheckedByAllReports
          checkbox.style.opacity = ''
          checkbox.disabled = false
        }
      })

      this.securityDocumentTargets.forEach(checkbox => {
        if (checkbox.dataset.visuallyOverridden === 'true') {
          // Remove Offering Documents override
          delete checkbox.dataset.visuallyOverridden
          delete checkbox.dataset.actualChecked
          delete checkbox.dataset.actualDisabled
          checkbox.style.opacity = ''
          checkbox.parentElement.style.opacity = ''
        }
        if (checkbox.dataset.allReportsOverridden === 'true') {
          // Remove All Reports override
          delete checkbox.dataset.allReportsOverridden
          delete checkbox.dataset.actualCheckedByAllReports
          delete checkbox.dataset.actualDisabledByAllReports
          checkbox.style.opacity = ''
          checkbox.parentElement.style.opacity = ''
        }
      })

      // Then restore initial states
      this.initialState.reports.forEach(({ element, checked }) => {
        element.checked = checked
      })
      this.initialState.securityDocs.forEach(({ element, checked, disabled }) => {
        element.checked = checked
        element.disabled = disabled
      })
      this.updateCount()
    }
  }

  handleAllReportsChange(event) {
    const isChecked = event.target.checked
    console.log('All Reports changed to:', isChecked)
    this.applyAllReportsOverride(isChecked)
    this.updateCount()
  }

  applyAllReportsOverride(isChecked) {
    // Track that All Reports state has changed
    this.allReportsStateChanged = true

    if (isChecked) {
      console.log('Applying All Reports override')
      // Apply visual override to ALL checkboxes (quote-level and security-level)

      // Override quote-level checkboxes (except All Reports itself)
      this.reportCheckboxTargets.forEach(checkbox => {
        if (checkbox.dataset.field !== 'include_all_reports') {
          // Store actual state
          if (!checkbox.dataset.allReportsOverridden) {
            checkbox.dataset.actualCheckedByAllReports = checkbox.checked ? 'true' : 'false'
            checkbox.dataset.allReportsOverridden = 'true'

            // Visually check and disable
            checkbox.checked = true
            checkbox.disabled = true
            checkbox.style.opacity = '0.7'
          }
        }
      })

      // Override security-level checkboxes
      if (this.hasSecurityDocumentTarget) {
        this.securityDocumentTargets.forEach(checkbox => {
          if (!checkbox.dataset.allReportsOverridden) {
            // Store actual state
            checkbox.dataset.actualCheckedByAllReports = checkbox.checked ? 'true' : 'false'
            checkbox.dataset.actualDisabledByAllReports = checkbox.disabled ? 'true' : 'false'
            checkbox.dataset.allReportsOverridden = 'true'

            // Check if this document is larger than recommended size (disabled due to page count)
            const isTooLarge = checkbox.disabled && checkbox.parentElement.querySelector('.text-error-600')

            if (isTooLarge) {
              // Don't check oversized documents - keep them unchecked and disabled
              console.log('Skipping oversized document for All Reports:', checkbox.value)
            } else {
              // Visually check and disable only eligible documents
              checkbox.checked = true
              checkbox.disabled = true
              checkbox.style.opacity = '0.7'
              checkbox.parentElement.style.opacity = '0.8'
            }
          }
        })
      }
    } else {
      // Remove visual override and restore actual states
      console.log('Removing All Reports override')

      // Restore quote-level checkboxes
      this.reportCheckboxTargets.forEach(checkbox => {
        if (checkbox.dataset.allReportsOverridden === 'true') {
          checkbox.checked = checkbox.dataset.actualCheckedByAllReports === 'true'
          checkbox.disabled = false
          checkbox.style.opacity = ''

          delete checkbox.dataset.actualCheckedByAllReports
          delete checkbox.dataset.allReportsOverridden
        }
      })

      // Restore security-level checkboxes, but check if Offering Documents override should still apply
      if (this.hasSecurityDocumentTarget) {
        // Check if Offering Documents is still selected
        const offeringDocsCheckbox = this.reportCheckboxTargets.find(cb => cb.dataset.field === 'include_offering_documents')
        const offeringDocsStillSelected = offeringDocsCheckbox && offeringDocsCheckbox.checked

        this.securityDocumentTargets.forEach(checkbox => {
          if (checkbox.dataset.allReportsOverridden === 'true') {
            // Store the actual states before removing All Reports override data
            const actualChecked = checkbox.dataset.actualCheckedByAllReports === 'true'
            const actualDisabled = checkbox.dataset.actualDisabledByAllReports === 'true'

            // Remove All Reports override data
            delete checkbox.dataset.actualCheckedByAllReports
            delete checkbox.dataset.actualDisabledByAllReports
            delete checkbox.dataset.allReportsOverridden

            // If Offering Documents is still selected, apply its override instead
            if (offeringDocsStillSelected) {
              console.log(`Applying Offering Documents override to doc ${checkbox.value} after removing All Reports override`)
              // Store the actual state as the actual state for Offering Documents override
              checkbox.dataset.actualChecked = actualChecked ? 'true' : 'false'
              checkbox.dataset.actualDisabled = actualDisabled ? 'true' : 'false'
              checkbox.dataset.visuallyOverridden = 'true'

              // Apply Offering Documents visual override
              checkbox.indeterminate = false
              checkbox.checked = true
              checkbox.disabled = true
              checkbox.style.opacity = '0.7'
              checkbox.parentElement.style.opacity = '0.8'
            } else {
              // No override needed - restore to actual state
              checkbox.checked = actualChecked
              checkbox.disabled = actualDisabled
              checkbox.style.opacity = ''
              checkbox.parentElement.style.opacity = ''
            }
          }
        })
      }
    }
  }

  handleOfferingDocumentsCheckboxChange(event) {
    // Don't process if we're currently restoring state
    if (this.isRestoringState) {
      console.log('Skipping offering documents change during restoration')
      return
    }

    const checkbox = event.target
    const isChecked = checkbox.checked

    console.log('Offering Documents checkbox changed to:', isChecked)

    // Apply the override
    this.handleOfferingDocumentsChange(isChecked)

    // Update the count after applying the override
    this.updateCount()
  }

  handleOfferingDocumentsChange(isChecked) {
    if (!this.hasSecurityDocumentTarget) return

    console.log('Applying Offering Documents override to:', isChecked)

    if (isChecked) {
      // Apply visual override to all security documents
      this.securityDocumentTargets.forEach(checkbox => {
        // Only process checkboxes that aren't already overridden
        if (checkbox.dataset.visuallyOverridden !== 'true') {
          // Store the actual state in data attributes
          checkbox.dataset.actualChecked = checkbox.checked ? 'true' : 'false'
          checkbox.dataset.actualDisabled = checkbox.disabled ? 'true' : 'false'

          console.log(`Storing state for doc ${checkbox.value}: checked=${checkbox.checked}, disabled=${checkbox.disabled}`)

          // Only override if not originally disabled
          if (!checkbox.disabled) {
            // Add data attribute to mark as visually overridden
            checkbox.dataset.visuallyOverridden = 'true'

            // Visually check the checkbox
            checkbox.indeterminate = false
            checkbox.checked = true

            // Disable interaction to prevent changes
            checkbox.disabled = true

            // Add visual styling to indicate override
            checkbox.style.opacity = '0.7'
            checkbox.parentElement.style.opacity = '0.8'

            console.log(`Applied override to doc ${checkbox.value}`)
          }
        }
      })
    } else {
      // Remove visual override and restore actual states
      console.log('Removing offering documents override')

      this.securityDocumentTargets.forEach(checkbox => {
        if (checkbox.dataset.visuallyOverridden === 'true') {
          console.log(`Restoring doc ${checkbox.value}: actualChecked=${checkbox.dataset.actualChecked}, actualDisabled=${checkbox.dataset.actualDisabled}`)

          // Restore the actual state from data attributes
          checkbox.checked = checkbox.dataset.actualChecked === 'true'
          checkbox.disabled = checkbox.dataset.actualDisabled === 'true'

          // Remove override markers
          delete checkbox.dataset.visuallyOverridden
          delete checkbox.dataset.actualChecked
          delete checkbox.dataset.actualDisabled

          // Remove visual styling
          checkbox.style.opacity = ''
          checkbox.parentElement.style.opacity = ''

          console.log(`Restored doc ${checkbox.value}: checked=${checkbox.checked}, disabled=${checkbox.disabled}`)
        }
      })
    }
  }


  updateDocumentSelectionDisplay(documentData) {
    console.log('=== updateDocumentSelectionDisplay called ===')
    console.log('documentData:', documentData)
    console.log('securityDocumentData available:', this.securityDocumentData)
    console.log('collectedSecurityInfo available:', this.collectedSecurityInfo)
    console.log('collectedSecurityInfo keys:', this.collectedSecurityInfo ? Array.from(this.collectedSecurityInfo.keys()) : 'none')

    // Find the Document Selection widget container by its header text
    const documentWidgetHeader = Array.from(document.querySelectorAll('h3')).find(h3 =>
      h3.textContent.includes('Document Selection')
    )

    if (!documentWidgetHeader) {
      console.log('Document Selection header not found')
      return
    }

    // Find the parent container that holds the whole Document Selection widget
    let documentWidget = documentWidgetHeader.closest('.bg-white')
    if (!documentWidget) {
      // Try alternative selector
      documentWidget = documentWidgetHeader.closest('[class*="card"]')
    }
    if (!documentWidget) {
      // Fallback to just the parent div
      documentWidget = documentWidgetHeader.parentElement.parentElement
    }
    console.log('Found document widget:', documentWidget)

    // Update the Selected Reports section to reflect current state
    this.updateSelectedReportsSection(documentWidget, documentData)

    // Find or create the security documents section
    let securityDocsSection = null
    const securityDocsHeader = Array.from(documentWidget.querySelectorAll('h4')).find(h4 =>
      h4.textContent.includes('Security Documents:')
    )
    console.log('Found security docs header:', securityDocsHeader)

    if (securityDocsHeader) {
      // The section structure is: parent div contains h4 and div.space-y-2
      const parentDiv = securityDocsHeader.parentElement
      securityDocsSection = parentDiv.querySelector('.space-y-2')
      console.log('Found existing security docs section:', securityDocsSection)
    }

    // If no security documents section exists, we need to create it
    if (!securityDocsSection) {
      console.log('Creating new security documents section')

      // Find where to insert the section
      const selectedReportsHeader = Array.from(documentWidget.querySelectorAll('h4')).find(h4 =>
        h4.textContent.includes('Selected Reports:')
      )
      const selectedReportsSection = selectedReportsHeader ? selectedReportsHeader.parentElement : null
      console.log('Selected reports section:', selectedReportsSection)

      const securityDocsSectionHtml = `
        <div class="border-t pt-4">
          <h4 class="text-sm font-medium text-neutral-700 mb-2">Security Documents:</h4>
          <div class="space-y-2"></div>
        </div>
      `

      if (selectedReportsSection) {
        // Insert after Selected Reports section
        selectedReportsSection.insertAdjacentHTML('afterend', securityDocsSectionHtml)
        const newSection = selectedReportsSection.nextElementSibling
        securityDocsSection = newSection.querySelector('.space-y-2')
      } else {
        // No Selected Reports section, insert at the beginning of left column
        const leftColumn = documentWidget.querySelector('.lg\\:col-span-2')
        if (leftColumn) {
          leftColumn.insertAdjacentHTML('afterbegin', securityDocsSectionHtml)
          securityDocsSection = leftColumn.querySelector('.space-y-2')
        }
      }

      console.log('Created new security docs section:', securityDocsSection)
    }

    if (!securityDocsSection) {
      console.log('Could not find or create security docs section, returning')
      return
    }

    // Clear existing security documents display
    securityDocsSection.innerHTML = ''

    // Handle "All reports" selection - show only eligible security documents (≤MAX_PAGE_COUNT pages)
    if (documentData.include_all_reports === 'true') {
      console.log('All reports selected - showing All documents includingonly eligible security documents')

      if (this.securityDocumentData && this.securityDocumentData.length > 0) {
        this.securityDocumentData.forEach(securityData => {
          if (securityData.documents && securityData.documents.length > 0) {
            // Filter out documents with more than MAX_PAGE_COUNT (TERM_SHEET is always allowed)
            const eligibleDocs = securityData.documents.filter(doc =>
              doc.document_type === TERM_SHEET || !doc.page_count || doc.page_count <= MAX_PAGE_COUNT
            )

            if (eligibleDocs.length > 0) {
              const selectedSecurityDocs = []
              selectedSecurityDocs.push({
                securityName: securityData.name,
                isin: securityData.isin,
                documents: eligibleDocs
              })

              // Render this security's documents
              selectedSecurityDocs.forEach(securityDocInfo => {
                const securityDiv = document.createElement('div')
                securityDiv.className = 'text-sm'

                const docsHtml = securityDocInfo.documents.map(doc =>
                  `<span class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-neutral-100 text-neutral-700">
                    ${doc.name}
                  </span>`
                ).join('')

                securityDiv.innerHTML = `
                  <span class="font-medium">${securityDocInfo.securityName}</span>
                  <span class="text-neutral-500">(${securityDocInfo.isin})</span>
                  <div class="ml-4 mt-1 flex flex-wrap gap-2">
                    ${docsHtml}
                  </div>
                `

                securityDocsSection.appendChild(securityDiv)
              })
            }
          }
        })

        // Show the security documents section
        const securityDocsContainer = securityDocsSection.parentElement
        if (securityDocsContainer) {
          securityDocsContainer.style.display = ''
        }
      }

      return
    }

    // Handle "Offering Documents" selection - show only eligible documents (≤10 pages) or number defined in Document::MAX_PAGE_COUNT. TERM_SHEET is always allowed
    if (documentData.include_offering_documents === 'true') {
      console.log('Offering Documents selected - showing only eligible security documents')

      if (this.securityDocumentData && this.securityDocumentData.length > 0) {
        this.securityDocumentData.forEach(securityData => {
          if (securityData.documents && securityData.documents.length > 0) {
            // Filter out documents with more than 10 pages or number defined in Document::MAX_PAGE_COUNT (TERM_SHEET is always allowed)
            const eligibleDocs = securityData.documents.filter(doc =>
              doc.document_type === TERM_SHEET || !doc.page_count || doc.page_count <= MAX_PAGE_COUNT
            )

            if (eligibleDocs.length > 0) {
              const selectedSecurityDocs = []
              selectedSecurityDocs.push({
                securityName: securityData.name,
                isin: securityData.isin,
                documents: eligibleDocs
              })

              // Render this security's documents
              selectedSecurityDocs.forEach(securityDocInfo => {
                const securityDiv = document.createElement('div')
                securityDiv.className = 'text-sm'

                const docsHtml = securityDocInfo.documents.map(doc =>
                  `<span class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-neutral-100 text-neutral-700">
                    ${doc.name}
                  </span>`
                ).join('')

                securityDiv.innerHTML = `
                  <span class="font-medium">${securityDocInfo.securityName}</span>
                  <span class="text-neutral-500">(${securityDocInfo.isin})</span>
                  <div class="ml-4 mt-1 flex flex-wrap gap-2">
                    ${docsHtml}
                  </div>
                `

                securityDocsSection.appendChild(securityDiv)
              })
            }
          }
        })

        // Show the security documents section
        const securityDocsContainer = securityDocsSection.parentElement
        if (securityDocsContainer) {
          securityDocsContainer.style.display = ''
        }
      }

      return
    }

    // Build display for selected security documents
    const selectedSecurityDocs = []

    // Process persisted security documents
    if (documentData.security_documents) {
      console.log('Processing persisted security documents:', documentData.security_documents)
      Object.keys(documentData.security_documents).forEach(lineItemId => {
        const docIds = documentData.security_documents[lineItemId]
        console.log(`Processing persisted docs for line item ${lineItemId}:`, docIds)
        this.addSecurityDocsToDisplay(selectedSecurityDocs, lineItemId, docIds, true)
      })
    } else {
      console.log('No persisted security documents to process')
    }

    // Process unpersisted security documents (new line items)
    if (documentData.unpersisted_security_documents) {
      console.log('Processing unpersisted security documents:', documentData.unpersisted_security_documents)
      Object.keys(documentData.unpersisted_security_documents).forEach(securityId => {
        const docIds = documentData.unpersisted_security_documents[securityId]
        console.log(`Processing unpersisted docs for security ${securityId}:`, docIds)
        this.addSecurityDocsToDisplay(selectedSecurityDocs, securityId, docIds, false)
      })
    } else {
      console.log('No unpersisted security documents to process')
    }

    // Render the selected documents
    console.log('Selected security docs to render:', selectedSecurityDocs)
    console.log('Security docs section element:', securityDocsSection)

    if (selectedSecurityDocs.length > 0) {
      console.log(`Rendering ${selectedSecurityDocs.length} security document groups`)

      selectedSecurityDocs.forEach((securityDocInfo, index) => {
        console.log(`Rendering security ${index + 1}:`, securityDocInfo)

        const securityDiv = document.createElement('div')
        securityDiv.className = 'text-sm'

        const docsHtml = securityDocInfo.documents.map(doc =>
          `<span class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-neutral-100 text-neutral-700">
            ${doc.name}
          </span>`
        ).join('')

        securityDiv.innerHTML = `
          <span class="font-medium">${securityDocInfo.securityName}</span>
          <span class="text-neutral-500">(${securityDocInfo.isin})</span>
          <div class="ml-4 mt-1 flex flex-wrap gap-2">
            ${docsHtml}
          </div>
        `

        console.log('Appending security div to section')
        securityDocsSection.appendChild(securityDiv)
      })

      // Show the security documents section
      const securityDocsContainer = securityDocsSection.parentElement
      console.log('Security docs container:', securityDocsContainer)
      if (securityDocsContainer) {
        securityDocsContainer.style.display = ''
        console.log('Made security docs container visible')
      }
    } else {
      console.log('No security documents to render, hiding section')
      // Hide the security documents section if no documents are selected
      const securityDocsContainer = securityDocsSection.parentElement
      if (securityDocsContainer) {
        securityDocsContainer.style.display = 'none'
      }
    }
  }

  updateSelectedReportsSection(documentWidget, documentData) {
    console.log('=== updateSelectedReportsSection called ===')

    // Find the Selected Reports section
    let selectedReportsHeader = Array.from(documentWidget.querySelectorAll('h4')).find(h4 =>
      h4.textContent.includes('Selected Reports:')
    )

    let selectedReportsContainer
    let selectedReportsSection

    if (!selectedReportsHeader) {
      console.log('Selected Reports header not found, creating new section')

      // Create the Selected Reports section
      const securityDocsHeader = Array.from(documentWidget.querySelectorAll('h4')).find(h4 =>
        h4.textContent.includes('Security Documents:')
      )

      const selectedReportsSectionHtml = `
        <div>
          <h4 class="text-sm font-medium text-neutral-700 mb-2">Selected Reports:</h4>
          <div class="flex flex-wrap gap-2"></div>
        </div>
      `

      if (securityDocsHeader) {
        // Insert before Security Documents section
        const securityDocsSection = securityDocsHeader.parentElement
        securityDocsSection.insertAdjacentHTML('beforebegin', selectedReportsSectionHtml)
        selectedReportsSection = securityDocsSection.previousElementSibling
      } else {
        // Insert at the beginning of the left column
        const leftColumn = documentWidget.querySelector('.lg\\:col-span-2')
        if (leftColumn) {
          leftColumn.insertAdjacentHTML('afterbegin', selectedReportsSectionHtml)
          selectedReportsSection = leftColumn.firstElementChild
        }
      }

      if (selectedReportsSection) {
        selectedReportsHeader = selectedReportsSection.querySelector('h4')
        selectedReportsContainer = selectedReportsSection.querySelector('.flex')
      }
    } else {
      selectedReportsContainer = selectedReportsHeader.nextElementSibling
      selectedReportsSection = selectedReportsHeader.parentElement
    }

    if (!selectedReportsContainer) {
      console.log('Selected Reports container not found')
      return
    }

    // Clear and rebuild the selected reports tags
    selectedReportsContainer.innerHTML = ''

    // Add active report tags based on current documentData
    const reportTags = []

    // When "All reports" is selected, show all report types
    if (documentData.include_all_reports === 'true') {
      reportTags.push('All Reports')
      reportTags.push('Indicative Quote Summary')
      reportTags.push('Client Notes')
      reportTags.push('Debt Securities Snapshot')
      reportTags.push('Offering Documents')
    } else {
      // Normal selection display
      if (documentData.include_indicative_quote === 'true') {
        reportTags.push('Indicative Quote Summary')
      }
      if (documentData.include_issuer_reports === 'true') {
        reportTags.push('Offering Documents')
      }
      if (documentData.include_notes === 'true') {
        reportTags.push('Client Notes')
      }
      if (documentData.include_debt_securities_snapshot === 'true') {
        reportTags.push('Debt Securities Snapshot')
      }
    }

    console.log('Active report tags:', reportTags)

    // Use the selectedReportsSection we already have from above
    if (!selectedReportsSection) {
      selectedReportsSection = selectedReportsHeader.parentElement
    }

    if (reportTags.length > 0) {
      const tagsHtml = reportTags.map(tag =>
        `<span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-neutral-100 text-neutral-700">
          ${tag}
        </span>`
      ).join('')

      selectedReportsContainer.innerHTML = `<div class="flex flex-wrap gap-2">${tagsHtml}</div>`

      // Show the Selected Reports section
      if (selectedReportsSection) {
        selectedReportsSection.style.display = ''
      }
    } else {
      // Hide the entire Selected Reports section when no reports are selected
      console.log('No reports selected, hiding Selected Reports section')
      if (selectedReportsSection) {
        selectedReportsSection.style.display = 'none'
      }
    }
  }

  addSecurityDocsToDisplay(selectedSecurityDocs, identifier, docIds, isPersisted) {
    console.log(`=== addSecurityDocsToDisplay called ===`)
    console.log('identifier:', identifier, 'docIds:', docIds, 'isPersisted:', isPersisted)

    if (!docIds || docIds.length === 0) {
      console.log('No docIds provided, returning')
      return
    }

    let securityInfo = null
    let lineItemId = null

    if (isPersisted) {
      // For persisted line items, we need to find the security ID from the line item ID
      lineItemId = identifier
      console.log(`Looking for persisted line item ${lineItemId} in form`)

      // Look for the hidden ID field that matches this line item ID
      const lineItemIdInput = document.querySelector(`input[name*="[id]"][value="${lineItemId}"]`)
      if (lineItemIdInput) {
        // Found the line item, now find its security_id
        const lineItemContainer = lineItemIdInput.closest('.quote-line-item')
        if (lineItemContainer) {
          const securityIdElement = lineItemContainer.querySelector('[name*="[security_id]"]')
          if (securityIdElement) {
            const securityId = securityIdElement.value
            console.log(`Found security ID ${securityId} for line item ${lineItemId}`)
            securityInfo = this.findSecurityInfo(securityId)
          } else {
            console.log('Could not find security_id field in line item container')
          }
        } else {
          console.log('Could not find line item container')
        }
      } else {
        console.log(`Could not find input with value=${lineItemId}`)
      }
    } else {
      // For unpersisted line items, identifier is the security ID
      const securityId = identifier
      securityInfo = this.findSecurityInfo(securityId)
      console.log(`Found security info for unpersisted security ${securityId}:`, securityInfo)
    }

    if (!securityInfo) {
      console.log('No security info found, returning')
      return
    }

    // Don't add unknown securities
    if (securityInfo.name === 'Unknown Security') {
      console.log('Security info is unknown, skipping')
      return
    }

    // Find the documents for this security from our cached data
    const securityDocuments = []
    console.log('Looking for documents. securityDocumentData:', this.securityDocumentData)
    console.log('securityInfo:', securityInfo)
    console.log('docIds to find:', docIds)

    if (this.securityDocumentData) {
      const securityData = this.securityDocumentData.find(s => s.security_id.toString() === securityInfo.id.toString())
      console.log('Found securityData:', securityData)

      if (securityData && securityData.documents) {
        console.log('Available documents for security:', securityData.documents)
        docIds.forEach(docId => {
          const doc = securityData.documents.find(d => d.id.toString() === docId.toString())
          console.log(`Looking for doc ${docId}, found:`, doc)
          if (doc) {
            securityDocuments.push(doc)
          }
        })
      } else {
        console.log('No documents found in cached data for this security')
        // For unpersisted securities, we might need to get the document info from the modal's checkboxes
        if (!isPersisted) {
          console.log('Trying to get document info from modal checkboxes')
          docIds.forEach(docId => {
            const checkbox = document.querySelector(`input[type="checkbox"][value="${docId}"][data-security-id="${securityInfo.id}"]`)
            if (checkbox) {
              const label = checkbox.closest('label')
              if (label) {
                const nameSpan = label.querySelector('.text-neutral-900')
                const typeDiv = label.querySelector('.text-neutral-500')
                if (nameSpan) {
                  securityDocuments.push({
                    id: docId,
                    name: nameSpan.textContent.trim(),
                    document_type: typeDiv ? typeDiv.textContent.split('•')[0].trim() : 'Document'
                  })
                  console.log('Found document info from checkbox:', securityDocuments[securityDocuments.length - 1])
                }
              }
            }
          })
        }
      }
    } else {
      console.log('No cached security document data available')
    }

    console.log('Final securityDocuments for display:', securityDocuments)

    if (securityDocuments.length > 0) {
      selectedSecurityDocs.push({
        securityName: securityInfo.name,
        isin: securityInfo.isin,
        documents: securityDocuments
      })
    }
  }

  findSecurityInfo(securityId) {
    console.log(`=== findSecurityInfo called for securityId: ${securityId} ===`)
    console.log('Available collectedSecurityInfo:', this.collectedSecurityInfo)
    console.log('collectedSecurityInfo keys:', this.collectedSecurityInfo ? Array.from(this.collectedSecurityInfo.keys()) : 'none')

    // First, try to find from the collected security info (most reliable source)
    if (this.collectedSecurityInfo && this.collectedSecurityInfo.has(securityId.toString())) {
      const info = this.collectedSecurityInfo.get(securityId.toString())
      console.log('Found security in collected info:', info)
      return {
        id: securityId,
        name: info.securityName || 'Unknown Security',
        isin: info.isin || 'Unknown ISIN'
      }
    } else {
      console.log(`Security ${securityId} not found in collected info`)
    }

    // Try to find from cached security document data
    if (this.securityDocumentData) {
      console.log('Looking in cached security document data')
      const securityData = this.securityDocumentData.find(s => s.security_id.toString() === securityId.toString())
      if (securityData) {
        console.log('Found security in cached data:', securityData)
        return {
          id: securityId,
          name: securityData.name || 'Unknown Security',
          isin: securityData.isin || 'Unknown ISIN'
        }
      }
    }

    // Try to find security info from the quote-securities controller data
    const quoteSecuritiesController = document.querySelector('[data-controller*="quote-securities"]')
    console.log('Found quote-securities controller:', quoteSecuritiesController)

    if (quoteSecuritiesController) {
      const autoPopulateData = quoteSecuritiesController.dataset.autoPopulateSecurities
      console.log('Auto-populate data:', autoPopulateData)

      if (autoPopulateData) {
        try {
          const securities = JSON.parse(autoPopulateData)
          console.log('Parsed securities:', securities)

          const security = securities.find(s => s.id.toString() === securityId.toString())
          if (security) {
            console.log('Found security in auto-populate data:', security)
            return {
              id: security.id,
              name: security.name || 'Unknown Security',
              isin: security.isin || 'Unknown ISIN'
            }
          }
        } catch (e) {
          console.error('Error parsing auto-populate securities:', e)
        }
      }
    }

    // Fallback: try to find from line item elements in the DOM
    console.log('Trying to find from line item elements in DOM')
    const lineItems = document.querySelectorAll('.quote-line-item')
    console.log(`Found ${lineItems.length} line items in DOM`)

    for (const lineItem of lineItems) {
      const securityIdInput = lineItem.querySelector('[name*="[security_id]"]')
      console.log(`Checking line item, security_id input value: ${securityIdInput?.value}, looking for: ${securityId}`)

      if (securityIdInput && securityIdInput.value === securityId.toString()) {
        console.log('Found matching line item for security')

        // Try to find security name and ISIN from the display
        const securityDisplay = lineItem.querySelector('.security-display')
        if (securityDisplay) {
          const nameElement = securityDisplay.querySelector('.font-medium')
          const isinElement = securityDisplay.querySelector('.text-xs')

          if (nameElement && isinElement) {
            const name = nameElement.textContent.trim()
            const isinText = isinElement.textContent.trim()
            const isin = isinText.replace('ISIN: ', '').replace('ISIN:', '').trim()

            console.log('Found security info from DOM:', { name, isin })
            return {
              id: securityId,
              name: name || 'Unknown Security',
              isin: isin || 'Unknown ISIN'
            }
          }
        }

        // Alternative: try to get from the data attributes
        const securityIsin = lineItem.dataset.securityIsin
        if (securityIsin) {
          console.log('Found ISIN from data attribute:', securityIsin)
          // Try to get name from somewhere else or use a placeholder
          const nameEl = lineItem.querySelector('.security-display .font-medium')
          return {
            id: securityId,
            name: nameEl ? nameEl.textContent.trim() : `Security ${securityIsin}`,
            isin: securityIsin
          }
        }
      }
    }

    console.log('Could not find security info, returning unknown')
    return {
      id: securityId,
      name: 'Unknown Security',
      isin: 'Unknown ISIN'
    }
  }
}